import { atom } from 'jotai'
import { atomWithStorage } from 'jotai/utils'
import { Client } from '../services/api'

// Client Pipeline stages (Interview Prep is a separate board)
export type WorkflowStage =
  | 'onboarding'
  | 'clifton_strength'
  | 'pre_north_star'
  | 'north_star'
  | 'strategy_session'
  | 'deliverables_in_progress'
  | 'deliverables_complete'
  | 'linkedin_workshop'
  | 'market_ready'
  | 'complete'

export const WORKFLOW_STAGES: WorkflowStage[] = [
  'onboarding',
  'clifton_strength',
  'pre_north_star',
  'north_star',
  'strategy_session',
  'deliverables_in_progress',
  'deliverables_complete',
  'linkedin_workshop',
  'market_ready',
  'complete',
]

export const STAGE_LABELS: Record<WorkflowStage, string> = {
  onboarding: 'Onboarding',
  clifton_strength: 'Clifton Strengths',
  pre_north_star: 'Initial Consultation',
  north_star: 'North Star',
  strategy_session: 'Strategy Session',
  deliverables_in_progress: 'Deliverables in Progress',
  deliverables_complete: 'Deliverables Complete',
  linkedin_workshop: 'LinkedIn Workshop',
  market_ready: 'Market Ready',
  complete: 'Complete',
}

export const STAGE_COLORS: Record<WorkflowStage, string> = {
  onboarding: '#66bb6a',      // Green - Client Success
  clifton_strength: '#b388ff', // Purple - Coach/Dee
  pre_north_star: '#b388ff',  // Purple - Coach
  north_star: '#f48fb1',      // Pink - Resume Writers
  strategy_session: '#b388ff', // Purple - Coach/Dee
  deliverables_in_progress: '#f48fb1',  // Pink - Resume Writers
  deliverables_complete: '#f48fb1',    // Pink - Resume Writers
  linkedin_workshop: '#b388ff', // Purple - Coach/Dee
  market_ready: '#9e9e9e',    // Gray - Terminal
  complete: '#4caf50',        // Green - Complete
}

export const STAGE_TEAMS: Record<WorkflowStage, string> = {
  onboarding: 'Client Success',
  clifton_strength: 'Coach',
  pre_north_star: 'Coach',
  north_star: 'Resume Writers',
  strategy_session: 'Coach',
  deliverables_in_progress: 'Resume Writers',
  deliverables_complete: 'Resume Writers',
  linkedin_workshop: 'Coach',
  market_ready: 'Client',
  complete: 'Complete',
}

// Maps each stage to the Client field representing the active team role
export const STAGE_TEAM_FIELD: Partial<Record<WorkflowStage, keyof Client>> = {
  onboarding: 'clientSuccess',
  clifton_strength: 'coach',
  pre_north_star: 'coach',
  north_star: 'resumeWriter',
  strategy_session: 'coach',
  deliverables_in_progress: 'resumeWriter',
  deliverables_complete: 'resumeWriter',
  linkedin_workshop: 'coach',
}

// Atoms
export const clientsAtom = atom<Client[]>([])
export const clientsLoadingAtom = atom(true)
export const selectedClientAtom = atom<Client | null>(null)
export const clientDetailOpenAtom = atom(false)

// Dynamic Workflow Stage Definitions
export interface WorkflowStageDefinition {
  stageId: string
  label: string
  color: string
  textColor: string
  teamRole: string | null
  sortOrder: string | number
  isActive: boolean
  isSystem: boolean
  // What this stage hides, on the client record and in the portal alike
  // (see constants/stageVisibility). Absent means the stage hides nothing,
  // which is every stage by default.
  hiddenPanels?: string[]
  // MWA-391 — the client email sent when a client enters this stage (see
  // constants/stageEntryEmails). Absent or null means the stage sends nothing.
  entryEmail?: string | null
  createdAt?: string
  updatedAt?: string
  createdBy?: string
  updatedBy?: string
}

// Atoms for dynamic stage definitions
export const workflowStageDefinitionsAtom = atom<WorkflowStageDefinition[]>([])
export const workflowStagesLoadingAtom = atom(false)

// ---------------------------------------------------------------------------
// Job Search client scoping
// ---------------------------------------------------------------------------

/**
 * The client whose Job Search surfaces you were last looking at.
 *
 * Job Search lives at /clients/:id/opportunities and /clients/:id/networking,
 * so the sidebar links and the old un-scoped paths need a client id to send you
 * to. This remembers the last one rather than making you pick every time.
 *
 * NEVER trust it on its own. Signing out does not clear localStorage in this
 * app, so this can name a client the NEXT person to sign in has no access to.
 * Its only remaining use is the "Last opened" marker on the client index, which
 * tests it against the rows already scoped for that user — so an id they cannot
 * reach simply matches nothing.
 */
export const rememberedClientIdAtom = atomWithStorage<string | null>(
  'missfit.jobSearch.clientId',
  null
)

/**
 * The stages whose clients the Job Search surfaces will open.
 *
 * 'market_ready' is the active case. 'complete' is included because a finished
 * engagement still holds the client's whole application history and staff need
 * to read it back — before this, a completed client's Job Search was
 * unreachable entirely.
 *
 * Deliberately NOT used by AddJobDialog: creating a NEW application is a
 * different question from reading history, and that picker offers market-ready
 * clients only (MWA-224 AC #5).
 */
export const JOB_SEARCH_STAGES = ['market_ready', 'complete']

/**
 * Which clients a Job Search surface should offer, scoped to who is asking.
 *
 * Three places needed this answer and each derived it separately: the navbar
 * switcher, the bare-route index, and the redirect the index replaced. The
 * redirect had already drifted — it applied the market-ready filter to
 * networking too, so the bare /networking route offered a narrower set than
 * the switcher standing right next to it.
 *
 * The scoping itself:
 *
 *   job-applications — market-ready only. That page enforces the same gate,
 *     so offering anyone else just lands you on its "not on the market yet"
 *     screen.
 *
 *   networking — every client. A LinkedIn import lands during intake, long
 *     before a client is on the market, and this is the only staff way into
 *     their network, so a market-ready filter would strand that work.
 *
 * A rep with assigned clients sees only those. A manager sees everyone, with
 * their own assignments sorted first.
 */
export function scopeJobSearchClients<T extends ClientScopeShape>(params: {
  clients: T[]
  userEmail?: string
  isManager: boolean
  subPath: 'job-applications' | 'networking'
}): T[] {
  const { clients, userEmail, isManager, subPath } = params
  const assignedIds = new Set(
    userEmail
      ? clients
          .filter(
            (c) =>
              c.coach === userEmail ||
              c.clientSuccess === userEmail ||
              c.resumeWriter === userEmail ||
              c.assignedTo === userEmail
          )
          .map((c) => c.id)
      : []
  )
  const eligible = subPath === 'networking' ? clients : clients.filter(isJobSearchEligible)
  const base =
    !isManager && assignedIds.size > 0 ? eligible.filter((c) => assignedIds.has(c.id)) : eligible
  return [...base].sort((a, b) => {
    if (isManager) {
      const aMine = assignedIds.has(a.id) ? 0 : 1
      const bMine = assignedIds.has(b.id) ? 0 : 1
      if (aMine !== bMine) return aMine - bMine
    }
    return (a.name || a.email || '').localeCompare(b.name || b.email || '')
  })
}

/** The client fields scopeJobSearchClients needs. */
export interface ClientScopeShape {
  id: string
  name?: string
  email?: string
  coach?: string
  clientSuccess?: string
  // The fourth team seat, and the one this used to miss: a resume writer
  // assigned to a client saw the whole unscoped list instead of their own.
  resumeWriter?: string
  assignedTo?: string
  workflowStage?: string | null
}

/** Whether the Job Search surfaces will open for this client. */
export function isJobSearchEligible(client?: { workflowStage?: string | null } | null): boolean {
  return !!client?.workflowStage && JOB_SEARCH_STAGES.includes(client.workflowStage)
}
